import type {
  ByProjectResponse as GenByProjectResponse,
  HeaderPresetCreate as GenHeaderPresetCreate,
  HeaderPresetDetail as GenHeaderPresetDetail,
  HeaderPresetResponse,
  MigrationAffectedJob as GenMigrationAffectedJob,
  MigrationPreview as GenMigrationPreview,
  MigrationSlotConflict as GenMigrationSlotConflict,
  OrgPhotoResponse,
  PhotoReportTemplateCreate as GenPhotoReportTemplateCreate,
  PhotoReportTemplateResponse,
  PhotoReportTemplateUpdate as GenPhotoReportTemplateUpdate,
} from '@api/model';

// The runtime table config (rows/columns/cellRange/…) lives in table-types; it is
// the `table` variant's payload. Distinct from the legacy `TableConfig` below,
// which only the deprecated TableBuilder panel still uses.
import type {TableConfig as TableRuntimeConfig} from './table-types';
import type {ClientFillableConfig} from './client-fillable';
import type {PhotoBoxConfig} from './photo-box';
import type {TextMark} from './rich-text';
import type {
  CheckboxControlConfig,
  LogoImageConfig,
  SignatureControlConfig,
  SizedControlConfig,
  TextControlConfig,
} from './control-config';

/** Single source of truth for element types. `ElementType` is derived from this
 *  tuple so the runtime list and the union can never drift; every per-type dispatch
 *  (blankRequirement, PreviewElement, the toolbar control panels) is exhaustive over
 *  it, so adding an entry here surfaces a compile error at each site that forgot it. */
export const ELEMENT_TYPES = [
  'group',
  'header',
  'photo',
  'logo',
  'text',
  'table',
  'checkbox',
  'dropdown',
  'signature',
  'page_break',
  'page_number',
  'qr_code',
  'divider',
] as const;

export type ElementType = (typeof ELEMENT_TYPES)[number];

export interface ElementStyle {
  font_family?: string;
  font_size?: number;
  font_weight?: string;
  font_style?: string;
  text_decoration?: string;
  color?: string;
  background_color?: string;
  border_width?: number;
  border_style?: string;
  border_color?: string;
  border_radius?: number;
  padding?: string;
  opacity?: number;
  box_shadow?: string;
  text_align?: string;
}

export interface HeaderConfig {
  width?: 'full' | '1/2' | '2/3' | '1/3' | '1/4';
  display_type?: 'text' | 'date' | 'time' | 'datetime';
  value?: string;
  /** Bullet/number prefix for a header rendered as a list item (read by HeaderRenderer). */
  list_type?: 'ordered' | 'unordered';
  /** Styles on part of `label`, over the element's own `style` (DERP-307) — see `rich-text`. */
  label_marks?: TextMark[];
}

/** A photo slot on the page. The box and its overlay are `PhotoBoxConfig`,
 *  shared with the in-cell image input — see there for why. */
export interface PhotoSlotConfig extends ClientFillableConfig, PhotoBoxConfig {
  show_location_name?: boolean;
  allow_pan?: boolean;
}

export interface TableConfig {
  columns: {key: string; label: string; width?: number}[];
  min_rows: number;
  unlimited_rows: boolean;
}

export type SignatureConfig = ClientFillableConfig & SignatureControlConfig;

export interface CheckboxConfig extends ClientFillableConfig, CheckboxControlConfig {
  options: string[];
  multi_select: boolean;
}

export interface DropdownConfig extends ClientFillableConfig, SizedControlConfig {
  options: string[];
  default_value?: string;
  lock_value?: boolean;
}

/**
 * Non-destructive crop persisted in a template's config: the image fills the box
 * (object-fit: cover) at scale 1; scale zooms in and offset_x/offset_y (px, frame-center
 * origin) pan the visible area. Applied client-side as a CSS transform (see `cropTransform`
 * in ./crop); the original image is never modified — only the visible portion changes.
 *
 * snake_case field names because this is a serialized wire/persisted shape. NOT the same
 * model as the shared photo-capture `CropState` (src/lib/photo-capture), which is
 * top-left-origin within a fixed viewport, natural-relative scale, lowered to a pixel rect
 * for a server-side crop, and never persisted. The two are distinct types and never interconvert.
 */
export interface ImageCrop {
  scale: number;
  offset_x: number;
  offset_y: number;
}

export interface LogoConfig extends LogoImageConfig {
  crop?: ImageCrop;
}

export type TextConfig = ClientFillableConfig & TextControlConfig;

export interface DividerConfig {
  color?: string;
  thickness?: number;
  line_style?: 'solid' | 'dashed' | 'dotted';
}

export interface QrConfig {
  /** Content encoded into the QR code. Supports template variables (e.g. {{job_number}}). */
  value?: string;
  /** Rendered size in px (square). For flow QR codes this is the only size control. */
  size?: number;
  /** Error-correction level (higher = more redundancy, denser code). */
  ecc_level?: 'L' | 'M' | 'Q' | 'H';
}

/** Format presets for a page-number element:
 *  `plain` → "1", `page_n` → "Page 1", `page_n_of_m` → "Page 1 of 5". */
export const PAGE_NUMBER_FORMATS = ['plain', 'page_n', 'page_n_of_m'] as const;
export type PageNumberFormat = (typeof PAGE_NUMBER_FORMATS)[number];

/** Which margin band the number sits in. Height is fixed — the band is the page's
 *  own top/bottom margin — so the only positional choice is `PageNumberAlign`. */
export const PAGE_NUMBER_PLACEMENTS = ['top', 'bottom'] as const;
export type PageNumberPlacement = (typeof PAGE_NUMBER_PLACEMENTS)[number];

export const PAGE_NUMBER_ALIGNS = ['left', 'center', 'right'] as const;
export type PageNumberAlign = (typeof PAGE_NUMBER_ALIGNS)[number];

/**
 * A `page_number` element stamps the running page number in a fixed band — the
 * page's top or bottom margin, spanning the content width (DERP-245). It is NOT
 * freely positioned: no coordinates, no drag, no resize; `placement` picks the
 * band and `align` picks the side. Unlike every other element it renders on EVERY
 * page in its range rather than only the page it was dropped on — see
 * `pageNumberItems` in usePreviewLayout.
 *
 * Read it through `resolvePageNumberConfig` (./page-numbers), never directly.
 */
export interface PageNumberConfig {
  format?: PageNumberFormat;
  placement?: PageNumberPlacement;
  align?: PageNumberAlign;
  /** First physical page (1-indexed) that prints a number; earlier pages print none. */
  show_from?: number;
  /** The number printed on `show_from`; later pages count up from it. */
  start_at?: number;
}

/** Page range a header group displays on (1-indexed, inclusive).
 *  `to` omitted means "to the end" (no upper bound).
 *  First page only → { from: 1, to: 1 }; Every page → { from: 1 } (to undefined). */
export interface HeaderScope {
  from: number;
  to?: number;
}

/** Config for structural elements (group sentinels, page breaks) that carry no
 *  per-type settings. */
export type EmptyConfig = Record<string, never>;

/**
 * Fields shared by every element, independent of `type`. A `PhotoRequirement` is
 * this base intersected with a `{ type; config }` pair per element type (below),
 * so `switch (item.type)` narrows `item.config` to the matching config interface.
 */
export interface RequirementBase {
  id: string;
  label: string;
  description?: string;
  required: boolean;
  order: number;
  section?: string;
  style: ElementStyle;
  position_mode?: 'flow' | 'absolute';
  flow_width?: 'full' | '1/2' | '2/3' | '1/3' | '1/4';
  group_id?: string;
  group_label?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** Sentinel-only: group is the page header region (shown at the top of the page). */
  is_header?: boolean;
  /** Header-sentinel only: the page range the header displays on. Supersedes the
   *  legacy `repeat_on_every_page` boolean. See `getHeaderScope` for the back-compat
   *  derivation used when this is absent. */
  header_scope?: HeaderScope;
  /** @deprecated Header-sentinel only: legacy repeat flag, superseded by
   *  `header_scope`. Read only as a fallback for templates saved before the page-range
   *  model existed; new writes set `header_scope` instead. */
  repeat_on_every_page?: boolean;
  /** Header-sentinel only: id of the library preset this header was saved as or
   *  applied from. Used to avoid offering "Save as Preset" for an already-saved
   *  header. Cleared implicitly once the referenced preset no longer exists. */
  preset_id?: string;
}

/**
 * A template element, discriminated on `type` so each variant's `config` is
 * strictly typed. Narrowing on `type` (via `switch` or a type guard) removes the
 * need to hand-cast `config` in renderers/config panels.
 */
export type PhotoRequirement =
  | (RequirementBase & {type: 'header'; config: HeaderConfig})
  | (RequirementBase & {type: 'photo'; config: PhotoSlotConfig})
  | (RequirementBase & {type: 'logo'; config: LogoConfig})
  | (RequirementBase & {type: 'text'; config: TextConfig})
  | (RequirementBase & {type: 'table'; config: TableRuntimeConfig})
  | (RequirementBase & {type: 'checkbox'; config: CheckboxConfig})
  | (RequirementBase & {type: 'dropdown'; config: DropdownConfig})
  | (RequirementBase & {type: 'signature'; config: SignatureConfig})
  | (RequirementBase & {type: 'qr_code'; config: QrConfig})
  | (RequirementBase & {type: 'divider'; config: DividerConfig})
  | (RequirementBase & {type: 'group'; config: EmptyConfig})
  | (RequirementBase & {type: 'page_break'; config: EmptyConfig})
  | (RequirementBase & {type: 'page_number'; config: PageNumberConfig});

/** The `PhotoRequirement` variant for a specific element type, e.g.
 *  `RequirementOf<"photo">`. Use for renderer/props that only handle one type. */
export type RequirementOf<T extends ElementType> = Extract<PhotoRequirement, {type: T}>;

export interface PageSettings {
  size: 'A4' | 'Letter' | 'custom';
  orientation: 'portrait' | 'landscape';
  margin_top: number;
  margin_bottom: number;
  margin_left: number;
  margin_right: number;
  page_break_height?: number;
}

export interface GridSettings {
  columns: number;
  row_height: number;
  gap: number;
}

export interface DefaultStyles {
  font_family: string;
  font_size: number;
  color: string;
  background_color: string;
}

export interface TemplateSettings {
  page: PageSettings;
  grid: GridSettings;
  default_styles: DefaultStyles;
}

// ── Server DTOs ──────────────────────────────────────────────────────────────
// These mirror the Orval-generated wire models (never hand-write API types —
// see CLAUDE.md §3). Where the editor enriches a field, we reuse the generated
// envelope and override only `requirements`/`settings`/`elements` with the
// typed view model (`PhotoRequirement`/`TemplateSettings`). The `lib/api`
// façade adapts between these and the raw generated shapes at the boundary.

/** Org photo library entry. */
export type OrgPhoto = OrgPhotoResponse;

/**
 * Editor view model for a template: the generated response envelope with the
 * richly-typed `requirements`/`settings` the canvas edits in place.
 * (`created_at`/`updated_at` are generated as `unknown`; the façade narrows
 * them to ISO strings.)
 */
export type PhotoReportTemplate = Omit<
  PhotoReportTemplateResponse,
  'requirements' | 'settings' | 'created_at' | 'updated_at'
> & {
  requirements: PhotoRequirement[];
  settings: Partial<TemplateSettings>;
  created_at: string;
  updated_at: string;
};

export type ByProjectResponse = Omit<GenByProjectResponse, 'template'> & {
  template: PhotoReportTemplate;
};

export type PhotoReportTemplateCreate = Omit<
  GenPhotoReportTemplateCreate,
  'requirements' | 'settings'
> & {
  // The editor authors requirements/settings as its richer view-model types;
  // the façade casts to the generated shape. `project_ids` passes through as-is.
  requirements?: PhotoRequirement[];
  settings?: Partial<TemplateSettings>;
};

export type PhotoReportTemplateUpdate = Omit<
  GenPhotoReportTemplateUpdate,
  'requirements' | 'settings'
> & {
  requirements?: PhotoRequirement[];
  settings?: Partial<TemplateSettings>;
};

// ── Header presets (reusable header groups, org-scoped) ──────────────────────

export type HeaderPresetCreate = Omit<GenHeaderPresetCreate, 'elements'> & {
  /** The serialized header-group elements (sentinel + members). */
  elements: PhotoRequirement[];
};

/** Lightweight response returned by create / list. */
export type HeaderPreset = HeaderPresetResponse;

/** Full detail (GET /{id}/) — includes the serialized `elements` so it can be applied. */
export type HeaderPresetDetail = Omit<GenHeaderPresetDetail, 'elements'> & {
  elements: PhotoRequirement[];
};

// ── Migration preflight (DERP-254) ───────────────────────────────────────────
// These pass through the façade unchanged, but they are re-exported here for the
// same reason every other generated shape is: `lib/api` and `lib/types` are the
// only two files in this module that import from `@api/model`, so a component
// never has to know what the wire looks like.

/**
 * The preflight a Migrate confirmation is built from. `can_migrate` is the
 * server's verdict and the only thing the dialog may block on — the client must
 * not re-derive it from `conflicts` (see DERP-249's lesson about shadowing a
 * server decision: the two agree until the server grows a second reason to say
 * no, and then the client silently says yes).
 */
export type MigrationPreview = GenMigrationPreview;

/** One project slot the migration cannot take because a live template holds it. */
export type MigrationSlotConflict = GenMigrationSlotConflict;

/** A job holding a filled report that the migration would put at risk. */
export type MigrationAffectedJob = GenMigrationAffectedJob;

/**
 * Editable form state for the template being designed. Client-side view model
 * for the editor page (distinct from the `PhotoReportTemplateCreate` wire body
 * the façade builds from it on save).
 */
/**
 * The report-template shapes (DERP-166), in display order. Mirrors the backend
 * TemplateType. This tuple is the single source: the union below derives from
 * it, `TEMPLATE_TYPE_LABEL` is keyed by it, and every surface that lists the
 * types iterates it — so adding a third shape is one edit plus whatever the
 * compiler then demands.
 */
export const TEMPLATE_TYPES = ['photo_report', 'jac'] as const;

export type TemplateTypeValue = (typeof TEMPLATE_TYPES)[number];

export interface TemplateForm {
  name: string;
  description: string;
  /** The projects this template applies to (DERP-166 — reusable across many). */
  project_ids: string[];
  template_type: TemplateTypeValue;
  /** The service types this template applies to — a list for both shapes since
   *  DERP-226. For photo_report an empty list is the project's default template;
   *  a JAC template requires at least one. */
  service_type_ids: string[];
  requirements: PhotoRequirement[];
  settings: Partial<TemplateSettings>;
}
